import { supabase } from './supabaseClient';
import { Database, CommentType } from '../../types/supabase';
import { Comment, CommentResponse } from '../../types/campagne';

type CampaignComment = Database['public']['Tables']['campaign_comments']['Row'];
type CampaignCommentResponse = Database['public']['Tables']['campaign_comment_responses']['Row'];

/**
 * Service pour gérer les commentaires des campagnes Supabase
 */

// Fonction pour récupérer tous les commentaires d'une campagne avec leurs réponses
export const getCampaignComments = async (
  campaignId: string,
  commentType: CommentType
): Promise<{ data: Comment[] | null; error: Error | null }> => {
  try {
    // Récupérer les commentaires avec leurs réponses
    const { data: comments, error: commentsError } = await supabase
      .from('campaign_comments')
      .select(`
        *,
        campaign_comment_responses (*)
      `)
      .eq('campaign_id', campaignId)
      .eq('comment_type', commentType)
      .order('created_at', { ascending: true });

    if (commentsError) {
      console.error('Erreur lors de la récupération des commentaires:', commentsError);
      return { data: null, error: new Error(commentsError.message) };
    }

    if (!comments) {
      return { data: [], error: null };
    }

    // Convertir au format attendu par le composant
    const formattedComments: Comment[] = comments.map((comment: CampaignComment & { campaign_comment_responses: CampaignCommentResponse[] }) => ({
      id: comment.id,
      text: comment.text,
      author: comment.author_name,
      timestamp: comment.created_at || new Date().toISOString(),
      isResolved: comment.is_resolved || false,
      responses: (comment.campaign_comment_responses || []).map((response: CampaignCommentResponse) => ({
        id: response.id,
        text: response.text,
        author: response.author_name,
        timestamp: response.created_at || new Date().toISOString(),
      })) || []
    }));

    return { data: formattedComments, error: null };
  } catch (err) {
    console.error('Erreur lors de la récupération des commentaires:', err);
    return { data: null, error: err as Error };
  }
};

// Fonction pour ajouter un nouveau commentaire
export const addCampaignComment = async (
  campaignId: string,
  commentType: CommentType,
  text: string,
  authorName: string,
  authorId?: string
) => {
  try {
    const { data, error } = await supabase
      .from('campaign_comments')
      .insert({
        campaign_id: campaignId,
        comment_type: commentType,
        text,
        author_name: authorName,
        author_id: authorId || null,
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur lors de l\'ajout du commentaire:', error);
    return { data: null, error };
  }
};

// Fonction pour ajouter une réponse à un commentaire
export const addCommentResponse = async (
  commentId: string,
  text: string,
  authorName: string,
  authorId?: string
) => {
  try {
    const { data, error } = await supabase
      .from('campaign_comment_responses')
      .insert({
        comment_id: commentId,
        text,
        author_name: authorName,
        author_id: authorId || null,
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur lors de l\'ajout de la réponse:', error);
    return { data: null, error };
  }
};

// Fonction pour marquer un commentaire comme résolu/non résolu
export const toggleCommentResolution = async (commentId: string, isResolved: boolean) => {
  try {
    const { data, error } = await supabase
      .from('campaign_comments')
      .update({ is_resolved: isResolved })
      .eq('id', commentId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur lors de la mise à jour du statut du commentaire:', error);
    return { data: null, error };
  }
};

// Fonction pour supprimer un commentaire
export const deleteCampaignComment = async (commentId: string) => {
  try {
    const { error } = await supabase
      .from('campaign_comments')
      .delete()
      .eq('id', commentId);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error: any) {
    console.error('Erreur lors de la suppression du commentaire:', error);
    return { error };
  }
};

// Fonction pour supprimer une réponse
export const deleteCommentResponse = async (responseId: string) => {
  try {
    const { error } = await supabase
      .from('campaign_comment_responses')
      .delete()
      .eq('id', responseId);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error: any) {
    console.error('Erreur lors de la suppression de la réponse:', error);
    return { error };
  }
}; 